import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "#" },
      { name: "Real Estate", link: "realestate-solution" },
      { name: "Logistics", link: "logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🧠",
    title: "Generative AI & LLM Solutions",
    desc: "Custom GPT-4 / Claude / Gemini integrations, fine-tuned domain-specific LLMs, RAG pipelines, and AI copilots that automate knowledge work at scale.",
    tags: ["GPT-4", "LangChain", "RAG", "Fine-Tuning"],
    color: "#8B5CF6",
  },
  {
    icon: "👁️",
    title: "Computer Vision",
    desc: "Object detection, facial recognition, OCR, defect inspection, and real-time video analytics — deployed on cloud, edge, or embedded devices.",
    tags: ["YOLO", "OpenCV", "TensorFlow", "PyTorch"],
    color: "#3B82F6",
  },
  {
    icon: "💬",
    title: "Conversational AI & Chatbots",
    desc: "Omnichannel AI assistants with intent detection, multi-turn memory, sentiment analysis, and seamless CRM/helpdesk hand-off for 24/7 customer support.",
    tags: ["Rasa", "Dialogflow", "GPT-4", "WhatsApp"],
    color: "#10B981",
  },
  {
    icon: "📊",
    title: "Predictive Analytics",
    desc: "Demand forecasting, churn prediction, fraud detection, and dynamic pricing models that turn your historical data into confident business decisions.",
    tags: ["XGBoost", "Scikit-learn", "Prophet", "SQL"],
    color: "#F59E0B",
  },
  {
    icon: "🔄",
    title: "MLOps & AI Infrastructure",
    desc: "End-to-end ML pipelines with automated retraining, model versioning, A/B testing, drift monitoring, and one-click deployment to AWS / GCP / Azure.",
    tags: ["MLflow", "Kubeflow", "Docker", "Airflow"],
    color: "#EF4444",
  },
  {
    icon: "📝",
    title: "Natural Language Processing",
    desc: "Text classification, named entity recognition, summarisation, sentiment analysis, and multilingual translation for documents, emails, and social feeds.",
    tags: ["Hugging Face", "BERT", "spaCy", "NLTK"],
    color: "#EC4899",
  },
  {
    icon: "🎯",
    title: "Recommendation Engines",
    desc: "Collaborative and content-based filtering systems that boost revenue through hyper-personalised product, content, and offer recommendations.",
    tags: ["Collaborative Filtering", "Embeddings", "Spark", "Redis"],
    color: "#06B6D4",
  },
  {
    icon: "🤖",
    title: "Robotic Process Automation + AI",
    desc: "Intelligent document processing, data extraction, and workflow automation that eliminates repetitive tasks and slashes operational costs by up to 70%.",
    tags: ["UiPath", "Python", "OCR", "GPT-4"],
    color: "#6366F1",
  },
];

const TECH_STACK = [
  {
    category: "Frameworks",
    items: [
      { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tensorflow/tensorflow-original.svg", color: "#FF6F00" },
      { name: "PyTorch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pytorch/pytorch-original.svg", color: "#EE4C2C" },
      { name: "Scikit-learn", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/scikitlearn/scikitlearn-original.svg", color: "#F89939" },
      { name: "Keras", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/keras/keras-original.svg", color: "#D00000" },
      { name: "OpenCV", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/opencv/opencv-original.svg", color: "#5C3EE8" },
      { name: "Hugging Face", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#FFD21E" },
    ],
  },
  {
    category: "Languages",
    items: [
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "R", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/r/r-original.svg", color: "#276DC3" },
      { name: "Julia", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/julia/julia-original.svg", color: "#9558B2" },
      { name: "SQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "Scala", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/scala/scala-original.svg", color: "#DC322F" },
      { name: "C++", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg", color: "#00599C" },
    ],
  },
  {
    category: "Data & MLOps",
    items: [
      { name: "Apache Spark", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apachespark/apachespark-original.svg", color: "#E25A1C" },
      { name: "Airflow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apacheairflow/apacheairflow-original.svg", color: "#017CEE" },
      { name: "MLflow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#0194E2" },
      { name: "Kafka", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/apachekafka/apachekafka-original.svg", color: "#231F20" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
    ],
  },
  {
    category: "Cloud AI",
    items: [
      { name: "AWS SageMaker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "GCP Vertex AI", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
      { name: "Azure ML", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "OpenAI API", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#412991" },
      { name: "Pinecone", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#1ABFB8" },
      { name: "MongoDB Atlas", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Data Audit & Strategy", desc: "We assess your existing data assets, define success metrics, and design an AI roadmap aligned with your specific business KPIs — no generic templates.", color: "#8B5CF6" },
  { n: "02", icon: "🧹", title: "Data Engineering", desc: "Collection, cleaning, labelling, and feature engineering. High-quality data is the foundation — we build robust pipelines before touching a model.", color: "#3B82F6" },
  { n: "03", icon: "🧪", title: "Model Development", desc: "Baseline → experimentation → fine-tuning. We track every run with MLflow, iterate rapidly, and validate rigorously before promotion to staging.", color: "#10B981" },
  { n: "04", icon: "📊", title: "Evaluation & Explainability", desc: "Beyond accuracy — we measure fairness, robustness, and explainability (SHAP/LIME) so you understand why the model makes every decision.", color: "#F59E0B" },
  { n: "05", icon: "🚀", title: "Deployment & Integration", desc: "REST / gRPC APIs, real-time streaming inference, batch scoring, or edge deployment — packaged in Docker, managed with Kubernetes, monitored 24/7.", color: "#EF4444" },
  { n: "06", icon: "📈", title: "Monitoring & Retraining", desc: "Automated data-drift and model-performance alerts trigger retraining pipelines so your AI stays accurate as the real world evolves.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "LogiTrack AI",
    industry: "Logistics",
    desc: "An intelligent route optimisation and demand forecasting engine for a pan-India supply chain operator — cutting fuel costs 28% and reducing late deliveries from 18% to under 3%.",
    img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop&auto=format",
    tags: ["Python", "XGBoost", "Google Maps API", "AWS"],
    metrics: [{ val: "28%", lbl: "Fuel Saved" }, { val: "83%", lbl: "Fewer Late Deliveries" }, { val: "₹4Cr+", lbl: "Annual Savings" }],
    color: "#EF4444",
  },
  {
    title: "HealthTrack AI Diagnostics",
    industry: "Healthcare",
    desc: "A computer-vision triage assistant that analyses medical scans with 94% accuracy, prioritising critical cases and reducing radiologist review time by 40% across 200+ hospitals.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["PyTorch", "DICOM", "AWS SageMaker", "React"],
    metrics: [{ val: "94%", lbl: "Diagnostic Accuracy" }, { val: "40%", lbl: "Faster Reviews" }, { val: "200+", lbl: "Hospitals" }],
    color: "#8B5CF6",
  },
  {
    title: "FinEdge Fraud Shield",
    industry: "FinTech",
    desc: "A real-time transaction fraud detection system processing 50,000+ events per second with a 99.3% precision rate — saving ₹12Cr in prevented fraud in the first quarter of deployment.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop&auto=format",
    tags: ["Kafka", "XGBoost", "Python", "Redis"],
    metrics: [{ val: "99.3%", lbl: "Precision Rate" }, { val: "50k+", lbl: "Events/Second" }, { val: "₹12Cr", lbl: "Fraud Prevented" }],
    color: "#10B981",
  },
];

const FAQS = [
  {
    q: "Do we need a large dataset to get started with AI?",
    a: "Not necessarily. The data requirement depends on the problem. For fine-tuning an LLM, even 500–1,000 high-quality examples can produce excellent results. For computer vision, transfer learning lets us achieve strong accuracy with hundreds of images rather than millions. We always start with a data audit to tell you exactly what you need — and how to get it if you don't have it yet.",
  },
  {
    q: "How long does a typical AI/ML project take?",
    a: "A focused proof-of-concept (POC) — for example, a document classification model or a chatbot — typically takes 4–8 weeks. A production-grade AI product with data pipelines, model serving, monitoring, and integration takes 3–6 months. We always recommend starting with a scoped POC to validate value before committing to a full build.",
  },
  {
    q: "Can you integrate AI into our existing software?",
    a: "Absolutely. We expose AI capabilities as REST or gRPC APIs that drop into any existing web app, mobile app, ERP, or CRM. We've integrated AI into Salesforce, SAP, custom Django/Laravel apps, and React Native mobile apps. The integration is typically the fastest phase of a project.",
  },
  {
    q: "How do you ensure our data stays private and secure?",
    a: "We sign NDAs before any data sharing. All data is processed in isolated, encrypted environments. For sensitive industries (healthcare, finance), we work within your private cloud (AWS VPC, Azure Private Link) so data never leaves your infrastructure. We never use client data to train models for other customers.",
  },
  {
    q: "What is the difference between AI and ML, and which do we need?",
    a: "Machine Learning (ML) is a subset of AI where models learn patterns from data to make predictions (e.g. fraud detection, demand forecasting). AI is the broader field including rule-based systems, computer vision, NLP, and generative AI. Most business problems benefit from ML models; if you want a chatbot or document understanding system, Generative AI / LLMs are the right approach. We'll recommend the best fit after a free discovery call.",
  },
  {
    q: "Do you offer post-deployment model maintenance?",
    a: "Yes. All production AI deployments are backed by our MLOps support packages: automated drift monitoring, monthly performance reports, triggered retraining pipelines, and on-call model engineers. Models degrade as real-world data shifts — our maintenance keeps them accurate long-term.",
  },
];

const STATS = [
  { val: "180+", lbl: "AI Projects Delivered", icon: "🤖" },
  { val: "94%", lbl: "Avg Model Accuracy", icon: "🎯" },
  { val: "70%", lbl: "Avg Cost Reduction", icon: "📉" },
  { val: "48h", lbl: "POC Turnaround", icon: "⚡" },
];

const USE_CASES = [
  { industry: "Healthcare", icon: "🏥", examples: ["Medical image analysis", "Clinical NLP", "Patient risk scoring"], color: "#EF4444" },
  { industry: "FinTech", icon: "💳", examples: ["Fraud detection", "Credit scoring", "Algorithmic trading"], color: "#10B981" },
  { industry: "Retail", icon: "🛍️", examples: ["Recommendation engine", "Demand forecasting", "Visual search"], color: "#F59E0B" },
  { industry: "Logistics", icon: "🚚", examples: ["Route optimisation", "Predictive maintenance", "Warehouse automation"], color: "#3B82F6" },
  { industry: "EdTech", icon: "🎓", examples: ["Adaptive learning", "Auto-grading", "Learning analytics"], color: "#8B5CF6" },
  { industry: "Manufacturing", icon: "🏭", examples: ["Defect detection", "Yield optimisation", "Predictive downtime"], color: "#EC4899" },
];
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight the active item inside the dropdown
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "/#services"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}
// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["94% Model Accuracy", "70% Cost Reduction", "48h POC Delivery", "180+ AI Projects"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#1E1B4B 0%,#312E81 35%,#1E3A8A 70%,#0F172A 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Animated background particles */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[...Array(20)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: Math.random() * 4 + 2,
            height: Math.random() * 4 + 2,
            borderRadius: "50%",
            background: `rgba(139,92,246,${Math.random() * 0.4 + 0.1})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `pulse ${Math.random() * 3 + 2}s ease-in-out infinite`,
          }} />
        ))}
      </div>
      {/* Grid overlay */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(139,92,246,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(139,92,246,.08) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />
      {/* Glow blobs */}
      <div style={{ position: "absolute", top: -120, right: -120, width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle,rgba(139,92,246,.18),transparent 65%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(59,130,246,.12),transparent 65%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "rgba(255,255,255,.5)" }}>
          <Link to="/" style={{ color: "#A78BFA", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span><span>Services</span><span>›</span>
          <span style={{ color: "#fff", fontWeight: 600 }}>AI & ML Solutions</span>
        </div>

        <div className="aiml-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(139,92,246,.2)", border: "1px solid rgba(167,139,250,.4)", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#A78BFA", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#C4B5FD" }}>🤖 India's Leading AI & ML Company</span>
            </div>

            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#fff", lineHeight: 1.1, marginBottom: 20 }}>
              Turn Your Data Into<br />
              <span style={{ background: "linear-gradient(135deg,#A78BFA,#60A5FA,#34D399)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Intelligent Decisions
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,.7)", lineHeight: 1.8, maxWidth: 560, marginBottom: 36 }}>
              We build production-ready AI and ML solutions — from Generative AI copilots and computer vision systems to predictive analytics and MLOps infrastructure — that create measurable competitive advantage.
            </p>

            {/* Rotating metric */}
            <div style={{ background: "rgba(139,92,246,.15)", border: "1px solid rgba(167,139,250,.3)", borderRadius: 12, padding: "14px 20px", marginBottom: 32, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#C4B5FD", transition: "all .4s" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#7C3AED,#8B5CF6)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 24px rgba(139,92,246,.5)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(139,92,246,.6)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(139,92,246,.5)"; }}
              >🚀 Start AI Journey</a>
              <a href="#case-studies" style={{
                background: "rgba(255,255,255,.08)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "1px solid rgba(255,255,255,.2)",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.15)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}
              >📊 See Case Studies</a>
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["180+", "AI Projects"], ["94%", "Avg Accuracy"], ["70%", "Cost Reduction"], ["48h", "POC Delivery"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#A78BFA" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — AI Visual */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {/* Central brain */}
            <div style={{
              width: 260, height: 260, borderRadius: "50%",
              background: "radial-gradient(circle,rgba(139,92,246,.3),rgba(109,40,217,.1))",
              border: "2px solid rgba(167,139,250,.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 96, animation: "float 4s ease-in-out infinite",
              boxShadow: "0 0 80px rgba(139,92,246,.25)",
              position: "relative", zIndex: 2,
            }}>🧠</div>

            {/* Orbiting capability chips */}
            {[
              { label: "LLM / GPT-4", angle: 0, color: "#A78BFA" },
              { label: "Computer Vision", angle: 60, color: "#60A5FA" },
              { label: "Predictive ML", angle: 120, color: "#34D399" },
              { label: "NLP", angle: 180, color: "#F472B6" },
              { label: "MLOps", angle: 240, color: "#FBBF24" },
              { label: "AutoML", angle: 300, color: "#F87171" },
            ].map((chip, i) => {
              const rad = (chip.angle * Math.PI) / 180;
              const r = 180;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <div key={chip.label} style={{
                  position: "absolute",
                  left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)`,
                  transform: "translate(-50%,-50%)",
                  background: "rgba(15,23,42,.85)",
                  border: `1px solid ${chip.color}50`,
                  borderRadius: 99,
                  padding: "7px 14px",
                  fontSize: 12, fontWeight: 700,
                  color: chip.color,
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 20px ${chip.color}25`,
                  zIndex: 3,
                  animation: `float ${3.5 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`,
                }}>{chip.label}</div>
              );
            })}

            {/* Floating metric cards */}
            <div style={{ position: "absolute", top: -10, right: -24, background: "#fff", borderRadius: 16, padding: "12px 18px", boxShadow: "0 12px 40px rgba(139,92,246,.25)", border: "1.5px solid #DDD6FE", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#6D28D9" }}>94%</div>
              <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>Model Accuracy 🎯</div>
            </div>
            <div style={{ position: "absolute", bottom: 20, right: -20, background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(139,92,246,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff" }}>48h</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500 }}>POC Delivery ⚡</div>
            </div>
            <div style={{ position: "absolute", bottom: 40, left: -16, background: "#fff", borderRadius: 14, padding: "11px 15px", boxShadow: "0 8px 28px rgba(0,0,0,.15)", border: "1.5px solid #D1FAE5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>📉</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#065F46", fontFamily: "'Sora',sans-serif" }}>70% Cost Cut</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>Avg per AI project</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="aiml-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#fff" }} className="aiml-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: ".1em", textTransform: "uppercase" }}>What We Build</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>AI & ML Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From Generative AI copilots to production-grade ML pipelines — we build AI that creates real business value.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease",
                transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Explore Service →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Use Cases by Industry ─────────────────────────────────────────────────────
function UseCases() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="aiml-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: ".1em", textTransform: "uppercase" }}>Industries</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>AI Across Every Industry</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Domain-specific AI solutions that speak your industry's language and solve your industry's problems.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {USE_CASES.map((uc, i) => (
            <div key={uc.industry}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "28px 24px",
                border: `2px solid ${hov === i ? uc.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${uc.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${uc.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: `1px solid ${uc.color}20` }}>
                  {uc.icon}
                </div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A" }}>{uc.industry}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {uc.examples.map(ex => (
                  <div key={ex} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: uc.color, flexShrink: 0, display: "inline-block" }} />
                    <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="aiml-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Our AI/ML Toolkit</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>The battle-tested frameworks, libraries, and platforms powering our AI solutions.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#6D28D9,#8B5CF6)" : "#fff",
              borderColor: active === i ? "#8B5CF6" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(139,92,246,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#fff", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 16px ${tech.color}25` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#1E1B4B 0%,#1E3A8A 100%)" }} className="aiml-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#A78BFA", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#fff", marginTop: 8, marginBottom: 16 }}>From Raw Data to Real ROI</h2>
          <p style={{ color: "rgba(255,255,255,.6)", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A rigorous, transparent 6-stage methodology that turns data into decisions — and decisions into revenue.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "rgba(255,255,255,.06)", borderRadius: 20, padding: "32px 28px",
              border: `1px solid rgba(255,255,255,.1)`,
              backdropFilter: "blur(10px)",
              position: "relative", overflow: "hidden",
              transition: "all .3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,92,246,.15)"; e.currentTarget.style.borderColor = `${step.color}60`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.1)"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: "rgba(255,255,255,.04)", lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}25`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}40` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}20`, padding: "3px 10px", borderRadius: 99, display: "inline-block", marginBottom: 10 }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#fff", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "rgba(255,255,255,.6)", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="aiml-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#EF4444", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>AI That Moved the Needle</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real numbers from real deployments — not demos, not POCs, production AI systems in the wild.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="aiml-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 26px", borderRadius: 10, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}35`,
                  transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#6D28D9", border: "2px solid #DDD6FE",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.borderColor = "#C4B5FD"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#DDD6FE"; }}
          >View All AI Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="aiml-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Honest answers to the most common questions about AI/ML projects.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#8B5CF6" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(139,92,246,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#8B5CF6" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .25s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", aitype: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#8B5CF6" : "#E5E7EB"}`,
    background: focused === n ? "#F5F3FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(139,92,246,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#F8FAFC,#F5F3FF)" }} className="aiml-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#7C3AED", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Start Your AI Journey Today</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your data and goals — we'll deliver a free AI feasibility assessment within 48 hours.</p>
        </div>
        <div className="aiml-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#1E1B4B,#4C1D95,#6D28D9)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(109,40,217,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Free AI Feasibility Assessment</h3>
              <p style={{ color: "rgba(255,255,255,.7)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>Share your use case — we'll assess feasibility, recommend an approach, and estimate ROI. No commitment required.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🧠", "AI feasibility assessment for your use case"],
                ["📊", "Data readiness evaluation"],
                ["💰", "ROI projection & effort estimate"],
                ["🔒", "NDA signed before any discussion"],
                ["🗺️", "Recommended AI roadmap & stack"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(139,92,246,.35)" }}>🧠</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Assessment Requested! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our AI team will review your use case and deliver a free feasibility report within 48 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", aitype: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#6D28D9,#8B5CF6)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Describe Your AI Use Case</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more detail you share, the more specific our feasibility assessment will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="aiml-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Work Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="aiml-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>AI Service Needed</label>
                      <select name="aitype" value={form.aitype} onChange={handleChange} onFocus={() => setFocused("aitype")} onBlur={() => setFocused(null)} style={{ ...iStyle("aitype"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select service…</option>
                        {["Generative AI / LLM", "Computer Vision", "Predictive Analytics", "NLP / Text AI", "Recommendation Engine", "MLOps Setup", "AI Chatbot", "Not sure yet"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Use Case Description *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="Describe the problem you want AI to solve — what data do you have, what decision or output do you need, and what would success look like for your business?" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your data and ideas are completely safe. We sign NDAs and never share your information.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#C4B5FD" : "linear-gradient(135deg,#6D28D9,#8B5CF6)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(139,92,246,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "🤖 Get Free AI Assessment"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#8B5CF6"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#C4B5FD"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function AIMLDevelopment() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links        { display:flex; align-items:center; gap:4px; }
        .nav-cta          { display:flex !important; }
        .mob-menu-btn     { display:none; }

        .aiml-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .aiml-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .aiml-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .aiml-contact-grid { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid       { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .aiml-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .aiml-hero-grid  { gap:40px; }
          .footer-grid     { grid-template-columns:1fr 1fr; gap:32px; }
          .aiml-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .aiml-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .aiml-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .aiml-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid       { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links         { display:none !important; }
          .nav-cta           { display:none !important; }
          .mob-menu-btn      { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid     { grid-template-columns:1fr; }
          .aiml-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .aiml-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px) { .aiml-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px) { .aiml-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <ServicesGrid />
      <UseCases />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}